import React, { useEffect, useState } from 'react';
import { Users, Loader2, Search, Phone, MessageSquare, CalendarDays, Clock, TrendingUp, UserPlus } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { IntelligenceLayout, fetchWithAuth, MetricCard } from './IntelligenceLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
];

const METRIC_CONFIG = [
  { key: 'companies_researched', label: 'Companies Researched', icon: Search, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { key: 'decision_makers_identified', label: 'Decision Makers Found', icon: UserPlus, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  { key: 'outreach_attempts', label: 'Outreach Logged', icon: Phone, color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { key: 'followups_scheduled', label: 'Follow-ups Scheduled', icon: CalendarDays, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'responses_received', label: 'Responses Received', icon: MessageSquare, color: 'bg-green-50 text-green-700 border-green-200' },
  { key: 'meetings_scheduled', label: 'Meetings Scheduled', icon: Clock, color: 'bg-teal-50 text-teal-700 border-teal-200' },
];

const RecruiterActivityDashboard = ({ embedded = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');

  useEffect(() => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/intelligence/recruiter-activity?period=${period}`)
      .then(d => setData(d)).catch(() => {}).finally(() => setLoading(false));
  }, [period]);

  const metrics = data?.metrics || {};
  const trend = data?.activity_trend || [];
  const totalActivities = Object.values(metrics).reduce((s, v) => s + (v || 0), 0);

  return (
    <IntelligenceLayout title="Recruiter Activity" embedded={embedded}>
      <div className="p-6 space-y-6" data-testid="recruiter-activity-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="page-title">Recruiter Activity</h1>
            <p className="text-sm text-gray-500 mt-1">Track execution and outreach performance</p>
          </div>
          <div className="flex gap-2" data-testid="period-filters">
            {PERIODS.map(p => (
              <button key={p.value} onClick={() => setPeriod(p.value)} data-testid={`period-${p.value}`}
                className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all ${
                  period === p.value ? 'bg-[#0A1F44] text-white border-[#0A1F44]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#D4AF37]'
                }`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : (
          <>
            {/* Total Summary */}
            <div className="bg-[#0A1F44] rounded-xl p-5 text-white flex items-center justify-between" data-testid="total-summary">
              <div>
                <p className="text-4xl font-bold">{totalActivities}</p>
                <p className="text-sm text-gray-300 mt-1">Total Activities — {PERIODS.find(p => p.value === period)?.label}</p>
              </div>
              <TrendingUp className="h-10 w-10 text-[#D4AF37] opacity-60" />
            </div>

            {/* Metric Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {METRIC_CONFIG.map(m => (
                <div key={m.key} className={`rounded-xl border p-4 ${m.color}`} data-testid={`metric-${m.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <m.icon className="h-5 w-5" />
                    <span className="text-2xl font-bold">{metrics[m.key] || 0}</span>
                  </div>
                  <p className="text-xs font-semibold">{m.label}</p>
                </div>
              ))}
            </div>

            {/* Activity Trend Chart */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-testid="activity-chart">
              <h3 className="text-sm font-semibold text-[#0A1F44] mb-4">Activity Trend (Last 14 Days)</h3>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={trend} margin={{ left: 0, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => {
                    const dt = new Date(d + 'T00:00:00');
                    return `${dt.getMonth() + 1}/${dt.getDate()}`;
                  }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip labelFormatter={d => new Date(d + 'T00:00:00').toLocaleDateString()} />
                  <Area type="monotone" dataKey="activities" stroke="#D4AF37" fill="#D4AF37" fillOpacity={0.15} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Activity Source Info */}
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
              <h3 className="text-xs font-semibold text-[#0A1F44] mb-2 uppercase tracking-wider">How Activities Are Counted</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs text-gray-600">
                <p><strong>Companies Researched:</strong> Outreach records created</p>
                <p><strong>Decision Makers:</strong> Contacts added via CRM</p>
                <p><strong>Outreach Logged:</strong> Timeline entries created</p>
                <p><strong>Follow-ups:</strong> Scheduled follow-up dates</p>
                <p><strong>Responses:</strong> Status = Interested/Meeting/Client</p>
                <p><strong>Meetings:</strong> Status = Meeting Scheduled</p>
              </div>
            </div>
          </>
        )}
      </div>
    </IntelligenceLayout>
  );
};

export default RecruiterActivityDashboard;
